
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { IndicesPropertyCategoryItemObject } from '@/store/modules/Indices/IndicesPropertyCategoriesModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'indices-property-categories-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(
          translate('indicesPropertyCategoryUpdating'),
          [translate('indices')]
        );
      });

      const itemRes = await store.dispatch(
        Actions.GET_INDICES_PROPERTY_CATEGORY,
        route.params.id
      );

      const item = ref<IndicesPropertyCategoryItemObject>(itemRes.data.data);

      const goBack = () => {
        router.back();
      };

      const validationSchema = Yup.object().shape({
        titleAr: Yup.string()
          .required(() => translate('TITLE_AR_IS_REQUIRED_FIELD'))
          .label('Title Ar'),
        titleEn: Yup.string()
          .required(() => translate('TITLE_EN_IS_REQUIRED_FIELD'))
          .label('Title En'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const payload = {
          titleAr: values.titleAr,
          titleEn: values.titleEn,
          disablePriceAreas: item.value.disablePriceAreas,
          disableVolumeAreas: item.value.disableVolumeAreas,
          volume_index_ticker: item.value.volume_index_ticker,
          isActive: item.value.isActive,
          price_index_view_active: item.value.price_index_view_active,
          volume_index_view_active: item.value.volume_index_view_active,
          ppm_view_active: item.value.ppm_view_active,
          no_of_deals_view_active: item.value.no_of_deals_view_active,
          ...(item.value.slug ? { slug: item.value.slug } : {}),
        };

        await store.dispatch(Actions.UPDATE_INDICES_PROPERTY_CATEGORY, {
          id: item.value?.id,
          data: payload,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_INDICES_PROPERTY_CATEGORY'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'indices-property-categories-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        item,
        can,
      };
    },
  });
